import React, { Fragment } from "react";
import { useFormikContext } from "formik";
import get from "lodash/get";
import PanelHeader from "../PanelHeader";
import { addEntity, disableEntitySelection } from "utils/builder";
import { STATE_KEYS } from "utils/constants/state";
import { v4 as uuidv4 } from "uuid";
import BlockOption from "components/FormerEditor/BlockOption";
import { APPLICATION, PAGE } from "utils/constants/models";
import { useModelQueryId } from "utils/hooks";
import ENTITIES, {
  ENTITY_TYPE
} from "components/FormerEditor/common/constants/entity";
import {
  useAppAdmin,
  useAppEntities,
  useCommonEntitlementConfigData,
  useCouponPromotionCodes,
  useFeatureGroupFeatures,
  useManifestShortLinks,
  useProductsPrices,
  useShippingRates,
  useTaxRates
} from "utils/selectors";
import { getEntitiesForManifestContext } from "utils/manifest";
import { useUserFeature } from "utils/hooks/capabilities";
import { USER_CAPABILITY } from "utils/constants/capability";
import OverscrollContent from "components/OverscrollContent";

const BETA_REQUIRED_ENTITIES = [ENTITIES.PRODUCT_COLLECTION];

const BlockPicker = ({ entityTypes = [ENTITY_TYPE.COMPOUND] }) => {
  const { values, setValues, setTouched } = useFormikContext();
  const applicationUUID = useModelQueryId(APPLICATION);
  const manifestUUID = useModelQueryId(PAGE);
  const entities = get(values, STATE_KEYS.MANIFEST.ENTITIES, []);
  const admin = useAppAdmin(applicationUUID, manifestUUID);
  const { products, prices } = useProductsPrices();
  const { features, featureGroups } = useFeatureGroupFeatures();
  const shortLinks = useManifestShortLinks();
  const appEntities = useAppEntities();
  const taxRates = useTaxRates();
  const { coupons } = useCouponPromotionCodes();
  const shippingRates = useShippingRates();
  const betaEnabled = useUserFeature(USER_CAPABILITY.BETA);
  const entitlements = useCommonEntitlementConfigData();

  const context = get(values, STATE_KEYS.MANIFEST.CONTEXT);

  /**
   * List all of the app entities
   */
  const contextEntities = getEntitiesForManifestContext(appEntities, context);
  const options = contextEntities.reduce((memo, entity, entityIx) => {
    const canSelect = !disableEntitySelection(entity, entities);
    const block = (
      <BlockOption
        canSelect={canSelect}
        key={entityIx}
        {...entity}
        onClick={addEntity({
          uuid: uuidv4(),
          entity,
          values,
          entities,
          appEntities,
          entitlements,
          admin,
          products,
          prices,
          features,
          featureGroups,
          shortLinks,
          taxRates,
          coupons,
          shippingRates,
          setValues,
          setTouched
        })}
      />
    );
    const betaRequired = BETA_REQUIRED_ENTITIES.indexOf(entity.uid) > -1;
    const typeMatch = entityTypes.indexOf(entity.type) > -1;

    if ((betaEnabled || !betaRequired) && typeMatch) {
      memo.push(block);
    }

    return memo;
  }, []);

  return (
    <Fragment>
      <PanelHeader title="Select a block" />
      <OverscrollContent>{options}</OverscrollContent>
    </Fragment>
  );
};

export default BlockPicker;
